import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EngineError,
  fetchCaseFileBlob,
  listCaseFiles,
  setFileCourtVisible,
  uploadCaseFile,
  type CaseFile,
  fileCaseFileAsStipulation,
} from '../../api/engineDashboard';
import { formatDateTime } from '../../lib/labels';
import { openEngineEvidenceUrl } from '../EvidenceList';

interface Props {
  token: string;
  caseId: string;
  canMutate: boolean;
  /** Court cases: uploads can be shared into the clerk/judge view. */
  isCourt?: boolean;
  onUnauthorized: () => void;
}

const fmtSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const fileEmoji = (contentType: string): string =>
  contentType.startsWith('image/') ? '🖼️' : contentType === 'application/pdf' ? '📄' : '📎';

/**
 * Case file attachments: photos, leases, signed paper agreements. Bytes
 * stream through the Engine (private bucket — no public URLs); "View"
 * fetches with the session token and opens a local object URL.
 */
export function FilesPanel({ token, caseId, canMutate, isCourt, onUnauthorized }: Props) {
  const [files, setFiles] = useState<CaseFile[] | null>(null);
  const [label, setLabel] = useState('');
  const [shareWithCourt, setShareWithCourt] = useState(false);
  const [busy, setBusy] = useState(false);
  // Promote-to-stipulation two-press state (Danny, 2026-09-21).
  const [filingStip, setFilingStip] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handle401 = useCallback(
    (err: unknown): boolean => {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return true;
      }
      return false;
    },
    [onUnauthorized],
  );

  const load = useCallback(() => {
    listCaseFiles(token, caseId)
      .then((res) => setFiles(res.files))
      .catch((err: unknown) => {
        if (handle401(err)) return;
        setFiles([]);
      });
  }, [token, caseId, handle401]);

  useEffect(load, [load]);

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const row = await uploadCaseFile(token, caseId, file, label.trim() || undefined, shareWithCourt);
      setFiles((prev) => [row, ...(prev ?? [])]);
      setLabel('');
      setShareWithCourt(false);
    } catch (err) {
      if (handle401(err)) return;
      setError(err instanceof EngineError ? err.message : 'Upload failed — try again.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const view = async (f: CaseFile) => {
    setError(null);
    try {
      if (f.source === 'party_message') {
        await openEngineEvidenceUrl(`/api/v1/evidence/${f.id}/file`, f.file_name, token);
        return;
      }
      const blob = await fetchCaseFileBlob(token, f.id);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        // Popup blocked (or Zoom webview) — fall back to a download click.
        const a = document.createElement('a');
        a.href = url;
        a.download = f.file_name;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      if (handle401(err)) return;
      setError('Could not open the file.');
    }
  };

  return (
    <section className="card p-4">
      <div className="section-label">Files</div>
      {files === null ? (
        <p className="mt-2 text-sm text-gray-500">Loading…</p>
      ) : files.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">No files yet — photos, leases, agreements.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-sm">
              <span aria-hidden>{fileEmoji(f.content_type)}</span>
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-left text-brand hover:underline"
                title={`${f.file_name} · ${fmtSize(f.size_bytes)} · ${formatDateTime(f.created_at)}`}
                onClick={() => void view(f)}
              >
                {f.label || f.file_name}
              </button>
              {f.source === 'party_message' && (
                <span className="shrink-0 rounded-full bg-surface-muted px-1.5 py-0.5 text-[10px] text-gray-500" title="Sent by a party in the conversation (text or email)">
                  from party
                </span>
              )}
              {f.court_visible && (
                <span className="shrink-0 rounded-full bg-brand-subtle px-1.5 py-0.5 text-[10px] font-medium text-brand" title="Visible in the clerk/judge Documents view">
                  ⚖ court
                </span>
              )}
              {isCourt && canMutate && f.source !== 'party_message' && (
                <button
                  type="button"
                  className="shrink-0 text-[10px] text-gray-400 hover:text-brand"
                  title={f.court_visible ? 'Remove from the court view' : 'Share into the clerk/judge Documents view (audited)'}
                  onClick={() => {
                    const next = !f.court_visible;
                    setFiles((prev) => (prev ?? []).map((x) => (x.id === f.id ? { ...x, court_visible: next } : x)));
                    setFileCourtVisible(token, f.id, next).catch(() => {
                      setFiles((prev) => (prev ?? []).map((x) => (x.id === f.id ? { ...x, court_visible: !next } : x)));
                    });
                  }}
                >
                  {f.court_visible ? 'unshare' : 'share w/ court'}
                </button>
              )}
              {isCourt && canMutate && f.content_type === 'application/pdf' && f.source !== 'party_message' && (
                filingStip === f.id ? (
                  <span className="flex shrink-0 items-center gap-1 text-[10px]">
                    <button
                      type="button"
                      className="rounded bg-brand px-1.5 py-0.5 font-medium text-white"
                      title="Creates the signed Stipulation record from this PDF and shares it with the court — the case then shows Stipulation · signed everywhere"
                      onClick={async () => {
                        try {
                          await fileCaseFileAsStipulation(token, f.id);
                          setFilingStip(null);
                          setError(null);
                          window.location.reload();
                        } catch (err) {
                          setFilingStip(null);
                          setError(err instanceof EngineError ? err.message : 'Could not file the stipulation.');
                        }
                      }}
                    >
                      Confirm: file as signed Stip
                    </button>
                    <button type="button" className="text-gray-400 hover:text-gray-700" onClick={() => setFilingStip(null)}>✕</button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="shrink-0 text-[10px] text-gray-400 hover:text-brand"
                    title="This PDF is the executed Stipulation — file it in the Stipulation slot so the court sees the case as signed"
                    onClick={() => setFilingStip(f.id)}
                  >
                    file as Stip…
                  </button>
                )
              )}
              <span className="shrink-0 text-xs text-gray-400">{fmtSize(f.size_bytes)}</span>
            </li>
          ))}
        </ul>
      )}

      {canMutate && (
        <div className="mt-3 border-t border-surface-border pt-3">
          <input
            className="w-full rounded-md border border-surface-border px-3 py-1.5 text-sm"
            placeholder="Label (optional — e.g. Signed lease 2024)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={busy}
          />
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="image/*,application/pdf,.doc,.docx,.txt"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void upload(f);
            }}
          />
          {isCourt && (
            <label className="mt-2 flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={shareWithCourt}
                onChange={(e) => setShareWithCourt(e.target.checked)}
                disabled={busy}
              />
              Share with the court (visible to clerks/judges)
            </label>
          )}
          <button
            type="button"
            className="btn-secondary mt-2 w-full"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? 'Uploading…' : 'Upload file'}
          </button>
          <p className="mt-1 text-xs text-gray-400">Photos, PDF, Word, text — up to 20 MB.</p>
        </div>
      )}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </section>
  );
}
